import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock3, XCircle, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const STATUS_META = {
  paid: { label: "Payment received · Preparing your order", tone: "ok", Icon: CheckCircle2 },
  awaiting_payment: { label: "Awaiting payment confirmation", tone: "wait", Icon: Clock3 },
  cancelled: { label: "This order was cancelled", tone: "bad", Icon: XCircle },
};

const SHIPPING_STEPS = [
  { key: "processing", label: "Preparing" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

export default function Track() {
  const [mobile, setMobile] = useState("");
  const [reference, setReference] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setOrder(null);
    try {
      const res = await axios.post(`${API}/orders/track`, { mobile, reference });
      setOrder(res.data);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setError(typeof raw === "string" ? raw : "Couldn't find that order. Please double-check the details.");
    } finally { setBusy(false); }
  };

  const reset = () => { setOrder(null); setError(""); setReference(""); };
  const meta = order ? (STATUS_META[order.payment_status] || STATUS_META.awaiting_payment) : null;

  return (
    <div className="track-page" data-testid="track-page">
      <header className="track-hero">
        <span className="lux-eyebrow dark">Order status</span>
        <h1>Track your order</h1>
        <p>Enter the mobile number you used at checkout and the AC-reference from your confirmation.</p>
      </header>

      {!order && (
        <form className="track-form" onSubmit={submit} data-testid="track-form">
          <label className="field">
            <span>Mobile number</span>
            <input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" required inputMode="numeric" data-testid="track-mobile-input" />
          </label>
          <label className="field">
            <span>Order reference</span>
            <input value={reference} onChange={e => setReference(e.target.value.toUpperCase())} placeholder="AC-XXXXXXXX" required data-testid="track-reference-input" />
          </label>
          <button className="lux-cta full" type="submit" disabled={busy} data-testid="track-submit">
            {busy ? "Looking up…" : "Find my order →"}
          </button>
          {error && <p className="checkout-error" data-testid="track-error" role="alert">{error}</p>}
          <p className="track-hint">Can&apos;t find your reference? <a href="https://wa.me/919769128394" target="_blank" rel="noreferrer">WhatsApp us</a> with your name and we&apos;ll look it up.</p>
        </form>
      )}

      {order && meta && (
        <section className="track-result" data-testid="track-result">
          <div className={`track-banner track-banner-${meta.tone}`}>
            <meta.Icon size={22} aria-hidden="true" />
            <div>
              <strong data-testid="track-status">{meta.label}</strong>
              <small>Placed {fmt(order.created_at)}</small>
            </div>
          </div>

          {order.payment_status === "paid" && (() => {
            const currentIdx = Math.max(0, SHIPPING_STEPS.findIndex(s => s.key === (order.shipping_status || "processing")));
            return (
              <div className="track-progress" data-testid="track-progress">
                {SHIPPING_STEPS.map((step, i) => (
                  <div key={step.key} className={`progress-step ${i <= currentIdx ? "done" : ""} ${i === currentIdx ? "current" : ""}`} data-testid={`progress-${step.key}`}>
                    <span className="progress-dot"/>
                    <p>{step.label}</p>
                  </div>
                ))}
                {order.tracking_number && (
                  <div className="tracking-note" data-testid="tracking-note">
                    Tracking: <strong>{order.tracking_number}</strong>{order.courier ? ` · ${order.courier}` : ""}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="track-meta">
            <div><span>Order reference</span><strong data-testid="track-reference">{order.id}</strong></div>
            <div><span>Customer</span><strong>{order.customer?.name}</strong></div>
            <div><span>Mobile</span><strong>{order.customer?.mobile}</strong></div>
            <div><span>Shipping to</span><strong>{order.customer?.city || "—"} {order.customer?.pincode || ""}</strong></div>
          </div>

          <div className="track-items">
            {(order.items || []).map((it, i) => (
              <article key={`${it.name}-${i}`} className="track-item">
                {it.image && <img src={it.image} alt={it.name} />}
                <div>
                  <h3>{it.name}</h3>
                  <small>Size {it.size}{it.color ? ` · ${it.color}` : ""} · Qty {it.qty}</small>
                </div>
                <strong>{money((it.price || 0) * (it.qty || 1))}</strong>
              </article>
            ))}
          </div>

          <div className="track-total">
            <span><Truck size={14} /> Order total</span>
            <strong>{money(order.total)}</strong>
          </div>

          <div className="track-actions">
            <button className="admin-refresh" onClick={reset} data-testid="track-new-lookup">Look up another order</button>
            <Link to="/shop" className="lux-text-link">Continue shopping →</Link>
          </div>
        </section>
      )}
    </div>
  );
}
